<template>
  <v-row>
    <v-dialog v-model="showDialog" max-width="720px">
      <v-card>
        <v-card-title>
          <span style="font-size: 20px;">
            {{ reportType && reportType.title ? st(reportType.title) : $t('wkt.admin.reports.download_dialog.title') }}
          </span>
        </v-card-title>
        <v-card-text @wheel="onWheel" class="report-download-dialog__content">
          <v-container class="report-download-dialog__container">
            <v-skeleton-loader v-if="loading" type="article, actions" />
            <div v-else class="report-download-dialog__body">
              <v-row class="report-download-dialog__row">
                <v-col cols="12" v-if="reportDescription">
                  <div style="color: rgba(0, 0, 0, 0.6);">
                    {{ reportDescription }}
                  </div>
                </v-col>
              </v-row>

              <v-row v-if="paramEntries.length > 0" class="report-download-dialog__row">
                <v-col cols="12">
                  <v-simple-table dense>
                    <tbody>
                      <tr v-for="param in paramEntries" :key="param.key">
                        <td style="width: 35%; font-weight: 600;">{{ param.label }}</td>
                        <td>{{ param.value }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>
              </v-row>

              <v-row v-if="editableFieldDefinitions.length > 0" class="report-download-dialog__row">
                <v-col
                  v-for="field in editableFieldDefinitions"
                  :key="field.key"
                  cols="12"
                  md="6"
                  class="report-download-dialog__field-col"
                >
                  <v-menu
                    v-if="field.type === 'date'"
                    v-model="dateMenus[field.key]"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="dateFormatted[field.key]"
                        :label="field.label"
                        :required="field.required"
                        :disabled="downloading"
                        v-on="on"
                        v-bind="attrs"
                        readonly
                        prepend-icon="mdi-calendar"
                      />
                    </template>
                    <v-date-picker
                      v-model="editableParams[field.key]"
                      :first-day-of-week="1"
                      :locale="$i18n.locale"
                      no-title
                      @input="dateMenus[field.key] = false; updateDateFormatted(field.key)"
                    />
                  </v-menu>
                  <v-select
                    v-else-if="field.type === 'month'"
                    v-model="editableParams[field.key]"
                    :items="monthOptions"
                    item-text="label"
                    item-value="value"
                    :label="field.label"
                    :required="field.required"
                    :disabled="downloading"
                  />
                  <v-select
                    v-else-if="field.type === 'year'"
                    v-model="editableParams[field.key]"
                    :items="yearOptions"
                    item-text="label"
                    item-value="value"
                    :label="field.label"
                    :required="field.required"
                    :disabled="downloading"
                  />
                  <v-select
                    v-else-if="field.type === 'workplace'"
                    v-model="editableParams[field.key]"
                    :items="workplaceOptions"
                    item-text="label"
                    item-value="value"
                    :label="field.label"
                    :required="field.required"
                    :disabled="downloading"
                  />
                  <v-select
                    v-else-if="field.type === 'department'"
                    v-model="editableParams[field.key]"
                    :items="departmentOptions"
                    item-text="label"
                    item-value="value"
                    :label="field.label"
                    :required="field.required"
                    :disabled="downloading"
                  />
                  <v-select
                    v-else-if="field.type === 'project'"
                    v-model="editableParams[field.key]"
                    :items="projectOptions"
                    item-text="label"
                    item-value="value"
                    :label="field.label"
                    :required="field.required"
                    :disabled="downloading"
                  />
                  <v-select
                    v-else-if="field.type === 'employee'"
                    v-model="editableParams[field.key]"
                    :items="employeeOptions"
                    item-text="label"
                    item-value="value"
                    :label="field.label"
                    :required="field.required"
                    :disabled="downloading"
                  />
                  <v-select
                    v-else-if="field.type === 'legal_entity'"
                    v-model="editableParams[field.key]"
                    :items="legalEntityOptions"
                    item-text="label"
                    item-value="value"
                    :label="field.label"
                    :required="field.required"
                    :disabled="downloading"
                  />
                  <v-select
                    v-else-if="field.type === 'grouping_axis'"
                    v-model="editableParams[field.key]"
                    :items="groupingAxisOptions"
                    item-text="label"
                    item-value="value"
                    :label="field.label"
                    :required="field.required"
                    :disabled="downloading"
                  />
                  <v-select
                    v-else-if="field.type === 'training_course'"
                    v-model="editableParams[field.key]"
                    :items="trainingCourseOptions"
                    item-text="label"
                    item-value="value"
                    :label="field.label"
                    :required="field.required"
                    :disabled="downloading"
                  />
                  <v-select
                    v-else-if="field.type === 'training_program'"
                    v-model="editableParams[field.key]"
                    :items="trainingProgramOptions"
                    item-text="label"
                    item-value="value"
                    :label="field.label"
                    :required="field.required"
                    :disabled="downloading"
                  />
                  <v-select
                    v-else-if="field.type === 'training_budget'"
                    v-model="editableParams[field.key]"
                    :items="trainingBudgetOptions"
                    item-text="label"
                    item-value="value"
                    :label="field.label"
                    :required="field.required"
                    :disabled="downloading"
                  />
                  <v-select
                    v-else-if="field.type === 'training_action'"
                    v-model="editableParams[field.key]"
                    :items="trainingActionOptions"
                    item-text="label"
                    item-value="value"
                    :label="field.label"
                    :required="field.required"
                    :disabled="downloading"
                  />
                </v-col>
              </v-row>

              <v-row v-if="formatOptions.length > 1" class="report-download-dialog__row">
                <v-col cols="12" md="6">
                  <v-select
                    v-model="selectedFormat"
                    :items="formatOptions"
                    item-text="label"
                    item-value="value"
                    :label="$t('wkt.admin.reports.download_dialog.format')"
                    :disabled="downloading"
                  />
                </v-col>
              </v-row>
            </div>
          </v-container>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showDialog = false">
            {{ $t('wkt.admin.common.dialog-buttons.cancel').toUpperCase() }}
          </v-btn>
          <v-btn color="#185EA9" text :loading="downloading" @click="downloadReport">
            {{ $t('wkt.admin.reports.download_dialog.download').toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import BaseModule from '../../common/BaseModule.js'
import api from '../../../api/worktime'
import gwt from '../../../api/gwt'

export default {
  name: 'ReportDownloadDialog',
  mixins: [BaseModule],
  data() {
    return {
      showDialog: false,
      loading: false,
      downloading: false,
      reportType: null,
      reportParams: {},
      editableParams: {},
      dateMenus: {},
      dateFormatted: {},
      workplaces: [],
      departments: [],
      projects: [],
      employees: [],
      legalEntities: [],
      trainingCourses: [],
      trainingPrograms: [],
      trainingBudgets: [],
      trainingActions: [],
      relatedReportTypes: [],
      selectedFormat: 'pdf'
    }
  },
  computed: {
    reportDescription() {
      if (!this.reportType || !this.reportType.module) {
        return ''
      }
      return this.$t('wkt.admin.reports.download_dialog.description')
    },
    formatOptions() {
      const options = (this.relatedReportTypes || []).map((reportType) => ({
        value: this.getFormatFromMimeType(reportType.downloadMimeType),
        label: this.getFormatLabel(this.getFormatFromMimeType(reportType.downloadMimeType))
      }))
      const uniqueOptions = []
      const seen = new Set()
      options.forEach((option) => {
        if (!seen.has(option.value)) {
          seen.add(option.value)
          uniqueOptions.push(option)
        }
      })
      if (uniqueOptions.length > 0) {
        return uniqueOptions
      }
      const format = this.inferDefaultFormat()
      return [{
        value: format,
        label: this.getFormatLabel(format)
      }]
    },
    editableFieldDefinitions() {
      const fixedParamKeys = new Set(
        Object.keys(this.reportParams || {}).filter((key) => {
          const value = this.reportParams[key]
          return value !== undefined && value !== null && value !== ''
        })
      )
      return this.parseFieldDefinitions(this.reportType && this.reportType.fields ? this.reportType.fields : '')
        .filter((field) => !fixedParamKeys.has(field.key))
    },
    monthOptions() {
      return [
        { value: 1, label: this.$t('wkt.admin.common.month-january') },
        { value: 2, label: this.$t('wkt.admin.common.month-february') },
        { value: 3, label: this.$t('wkt.admin.common.month-march') },
        { value: 4, label: this.$t('wkt.admin.common.month-april') },
        { value: 5, label: this.$t('wkt.admin.common.month-may') },
        { value: 6, label: this.$t('wkt.admin.common.month-june') },
        { value: 7, label: this.$t('wkt.admin.common.month-july') },
        { value: 8, label: this.$t('wkt.admin.common.month-august') },
        { value: 9, label: this.$t('wkt.admin.common.month-september') },
        { value: 10, label: this.$t('wkt.admin.common.month-october') },
        { value: 11, label: this.$t('wkt.admin.common.month-november') },
        { value: 12, label: this.$t('wkt.admin.common.month-december') }
      ]
    },
    yearOptions() {
      const currentYear = new Date().getFullYear()
      const options = []
      for (let year = currentYear + 2; year >= currentYear - 10; year -= 1) {
        options.push({ value: year, label: String(year) })
      }
      return options
    },
    workplaceOptions() {
      return (this.workplaces || []).map((item) => ({
        value: item.id,
        label: item.name
      }))
    },
    departmentOptions() {
      return (this.departments || []).map((item) => ({
        value: item.id,
        label: item.name
      }))
    },
    projectOptions() {
      return (this.projects || []).map((item) => ({
        value: item.id,
        label: item.shortName && item.description
          ? `${item.shortName} - ${item.description}`
          : (item.shortName || item.description || String(item.id))
      }))
    },
    employeeOptions() {
      return (this.employees || []).map((item) => ({
        value: item.id,
        label: `${item.lastname || ''}, ${item.firstname || ''}`.replace(/^,\s*/, '').trim()
      }))
    },
    legalEntityOptions() {
      return (this.legalEntities || []).map((item) => ({
        value: item.id,
        label: item.legalEntityName || item.name || String(item.id)
      }))
    },
    groupingAxisOptions() {
      return [
        { value: 'department', label: this.$t('wkt.admin.reports.grouping_axis.department') },
        { value: 'workplace', label: this.$t('wkt.admin.reports.grouping_axis.workplace') },
        { value: 'legal_entity', label: this.$t('wkt.admin.reports.grouping_axis.legal_entity') }
      ]
    },
    trainingCourseOptions() {
      return (this.trainingCourses || []).map((item) => ({
        value: item.id,
        label: item.reference && item.name
          ? `${item.reference} - ${item.name}`
          : (item.name || item.reference || String(item.id))
      }))
    },
    trainingProgramOptions() {
      return (this.trainingPrograms || []).map((item) => ({
        value: item.id,
        label: item.reference && item.name
          ? `${item.reference} - ${item.name}`
          : (item.name || item.reference || String(item.id))
      }))
    },
    trainingBudgetOptions() {
      return (this.trainingBudgets || []).map((item) => ({
        value: item.id,
        label: item.reference || String(item.id)
      }))
    },
    trainingActionOptions() {
      return (this.trainingActions || []).map((item) => ({
        value: item.id,
        label: item.reference && item.name
          ? `${item.reference} - ${item.name}`
          : (item.name || item.reference || String(item.id))
      }))
    },
    paramEntries() {
      return Object.keys(this.reportParams || {})
        .filter((key) => this.reportParams[key] !== undefined && this.reportParams[key] !== null && this.reportParams[key] !== '')
        .map((key) => ({
          key,
          label: this.getParamLabel(key),
          value: this.formatParamValue(this.reportParams[key])
        }))
    }
  },
  methods: {
    onWheel(event) {
      if (!event) {
        return
      }

      event.stopPropagation()
    },
    resolveReportTypeId(eventConfig, payload) {
      const directCandidate = eventConfig && eventConfig.reportTypeId != null
        ? eventConfig.reportTypeId
        : null

      if (directCandidate == null || directCandidate === '') {
        return null
      }

      if (typeof directCandidate === 'number') {
        return directCandidate
      }

      if (typeof directCandidate === 'string' && /^\d+$/.test(directCandidate)) {
        return Number(directCandidate)
      }

      const contexts = [
        eventConfig && eventConfig.menuContext ? eventConfig.menuContext : null,
        payload && typeof payload === 'object' ? payload : null
      ].filter(Boolean)

      for (const context of contexts) {
        if (Object.prototype.hasOwnProperty.call(context, directCandidate)) {
          return context[directCandidate]
        }
      }

      return directCandidate
    },
    loadReportType(reportTypeId) {
      if (typeof reportTypeId === 'number' || (typeof reportTypeId === 'string' && /^\d+$/.test(reportTypeId))) {
        api.getAnalyticsReportTypeById(
          reportTypeId,
          (data) => {
            this.reportType = data
            this.initializeEditableParams()
            this.resolveRelatedReportTypes()
          },
          (error) => {
            this.loading = false
            this.$emit('show-alert', {
              text: error?.response?.data?.message || this.$t('wkt.admin.reports.download_dialog.error.load_report'),
              color: 'error',
              timeout: 4000
            })
            this.showDialog = false
          }
        )
        return
      }

      this.loading = false
      this.$emit('show-alert', {
        text: this.$t('wkt.admin.reports.download_dialog.error.load_report'),
        color: 'error',
        timeout: 4000
      })
      this.showDialog = false
    },
    open(action, payload, eventConfig) {
      this.showDialog = true
      this.loading = true
      this.downloading = false
      this.reportType = null
      this.relatedReportTypes = []
      this.reportParams = eventConfig && eventConfig.reportParams ? { ...eventConfig.reportParams } : {}
      this.editableParams = {}
      this.dateMenus = {}
      this.dateFormatted = {}
      this.selectedFormat = 'pdf'
      const resolvedReportTypeId = this.resolveReportTypeId(eventConfig, payload)
      this.loadReportType(resolvedReportTypeId)
    },
    inferDefaultFormat() {
      const mimeType = this.reportType && this.reportType.downloadMimeType
      return this.getFormatFromMimeType(mimeType)
    },
    getFormatFromMimeType(mimeType) {
      if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        return 'xlsx'
      }
      return 'pdf'
    },
    parseFieldDefinitions(fieldsText) {
      if (!fieldsText || !fieldsText.trim()) {
        return []
      }
      return fieldsText
        .split(',')
        .map((field) => String(field || '').trim())
        .filter((field) => !!field)
        .map((field) => ({
          key: field.endsWith('?') ? field.slice(0, -1) : field,
          required: !field.endsWith('?')
        }))
        .map((field) => ({
          ...field,
          type: this.getFieldType(field.key),
          label: this.getParamLabel(field.key)
        }))
        .filter((field) => field.type !== 'unsupported')
    },
    getFieldType(fieldKey) {
      switch (fieldKey) {
        case 'start_date':
        case 'end_date':
          return 'date'
        case 'month':
          return 'month'
        case 'year':
          return 'year'
        case 'workplace_id':
          return 'workplace'
        case 'department_id':
          return 'department'
        case 'project_id':
          return 'project'
        case 'legal_entity_id':
          return 'legal_entity'
        case 'employee_id':
          return 'employee'
        case 'grouping_axis':
          return 'grouping_axis'
        case 'course_id':
        case 'training_course_id':
          return 'training_course'
        case 'training_program_id':
          return 'training_program'
        case 'training_budget_id':
          return 'training_budget'
        case 'training_action_id':
          return 'training_action'
        default:
          return 'unsupported'
      }
    },
    getParamLabel(key) {
      const paramKey = `wkt.admin.reports.params.${key}`
      return this.st(paramKey) !== paramKey ? this.st(paramKey) : key
    },
    getFormatLabel(format) {
      const normalized = String(format || '').toLowerCase()
      if (normalized === 'xlsx' || normalized === 'excel') {
        return this.$t('wkt.admin.reports.download_dialog.format_excel')
      }
      return this.$t('wkt.admin.reports.download_dialog.format_pdf')
    },
    initializeEditableParams() {
      const initialValues = {}
      this.ensureSelectableData()
      this.editableFieldDefinitions.forEach((field) => {
        const prefilledValue = this.reportParams[field.key]
        if (prefilledValue !== undefined && prefilledValue !== null && prefilledValue !== '') {
          initialValues[field.key] = this.normalizeEditableValue(field.key, prefilledValue)
          return
        }
        if (field.key === 'start_date') {
          const date = new Date()
          date.setMonth(date.getMonth() - 1)
          initialValues[field.key] = this.formatDateInputValue(date)
          return
        }
        if (field.key === 'end_date') {
          initialValues[field.key] = this.formatDateInputValue(new Date())
          return
        }
        if (field.key === 'year' && field.required) {
          initialValues[field.key] = new Date().getFullYear()
          return
        }
        initialValues[field.key] = null
      })
      this.editableParams = initialValues
      this.initializeDateFormatted()
    },
    initializeDateFormatted() {
      const menus = {}
      const formatted = {}
      this.editableFieldDefinitions.forEach((field) => {
        if (field.type === 'date') {
          menus[field.key] = false
          const val = this.editableParams[field.key]
          formatted[field.key] = val ? this.formatISOStringDateToLocalDate(val) : null
        }
      })
      this.dateMenus = menus
      this.dateFormatted = formatted
    },
    updateDateFormatted(fieldKey) {
      const val = this.editableParams[fieldKey]
      this.$set(this.dateFormatted, fieldKey, val ? this.formatISOStringDateToLocalDate(val) : null)
    },
    normalizeEditableValue(fieldKey, value) {
      if (fieldKey === 'start_date' || fieldKey === 'end_date') {
        if (typeof value === 'string') {
          return value.length >= 10 ? value.slice(0, 10) : value
        }
        if (value && typeof value === 'object' && value.year && value.month && value.day) {
          return `${value.year}-${String(value.month).padStart(2, '0')}-${String(value.day).padStart(2, '0')}`
        }
      }
      if (fieldKey === 'month' || fieldKey === 'year') {
        const parsed = parseInt(value, 10)
        return Number.isNaN(parsed) ? null : parsed
      }
      return value
    },
    formatDateInputValue(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return null
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    formatParamValue(value) {
      if (value == null || value === '') {
        return '-'
      }
      if (Array.isArray(value)) {
        return value.join(', ')
      }
      if (value && typeof value === 'object' && value.year && value.month && value.day) {
        return `${String(value.day).padStart(2, '0')}/${String(value.month).padStart(2, '0')}/${value.year}`
      }
      if (typeof value === 'object') {
        return JSON.stringify(value)
      }
      return String(value)
    },
    ensureSelectableData() {
      const fieldTypes = new Set(this.editableFieldDefinitions.map((field) => field.type))

      if (fieldTypes.has('workplace')) {
        this.loadWorkplaces()
      }
      if (fieldTypes.has('department') || fieldTypes.has('employee')) {
        this.loadDepartmentsAndEmployees()
      }
      if (fieldTypes.has('project')) {
        this.loadProjects()
      }
      if (fieldTypes.has('legal_entity')) {
        this.loadLegalEntities()
      }
      if (fieldTypes.has('training_course')) {
        this.loadTrainingCourses()
      }
      if (fieldTypes.has('training_program')) {
        this.loadTrainingPrograms()
      }
      if (fieldTypes.has('training_budget')) {
        this.loadTrainingBudgets()
      }
      if (fieldTypes.has('training_action')) {
        this.loadTrainingActions()
      }
    },
    loadWorkplaces() {
      this.workplaces = gwt.getWorkplacesByProfile ? gwt.getWorkplacesByProfile() : []
      if (this.workplaces && this.workplaces.length > 0) {
        return
      }
      api.getWorkPlaces(
        (data) => {
          this.workplaces = data || []
        },
        () => {},
        { omitGlobalFilters: true }
      )
    },
    loadDepartmentsAndEmployees() {
      this.departments = gwt.getDepartmentsByProfile ? gwt.getDepartmentsByProfile() : []
      if (this.departments && this.departments.length > 0) {
        this.loadEmployeesFromDepartments()
        return
      }
      api.getStaff(
        (data) => {
          this.departments = data || []
          this.loadEmployeesFromDepartments()
        },
        () => {},
        { omitGlobalFilters: true }
      )
    },
    loadProjects() {
      const cachedProjects = gwt.getProjects ? gwt.getProjects() : []
      if (cachedProjects && cachedProjects.length > 0) {
        this.projects = this.filterProjectsByProfile(cachedProjects)
        return
      }
      api.getProjects(
        (data) => {
          this.projects = this.filterProjectsByProfile(data || [])
        },
        () => {},
        { omitGlobalFilters: true }
      )
    },
    loadLegalEntities() {
      const cachedLegalEntities = gwt.getLegalEntities ? gwt.getLegalEntities() : []
      if (cachedLegalEntities && cachedLegalEntities.length > 0) {
        this.legalEntities = cachedLegalEntities
        return
      }
      api.getLegalEntities(
        (data) => {
          this.legalEntities = data || []
        },
        () => {},
        { omitGlobalFilters: true }
      )
    },
    loadTrainingCourses() {
      if (this.trainingCourses && this.trainingCourses.length > 0) {
        return
      }
      api.getTrainingCourses(
        (data) => {
          this.trainingCourses = data || []
        },
        () => {}
      )
    },
    loadTrainingPrograms() {
      if (this.trainingPrograms && this.trainingPrograms.length > 0) {
        return
      }
      api.getTrainingPrograms(
        (data) => {
          this.trainingPrograms = data || []
        },
        () => {}
      )
    },
    loadTrainingBudgets() {
      if (this.trainingBudgets && this.trainingBudgets.length > 0) {
        return
      }
      api.getTrainingBudgets(
        (data) => {
          this.trainingBudgets = data || []
        },
        () => {}
      )
    },
    loadTrainingActions() {
      if (this.trainingActions && this.trainingActions.length > 0) {
        return
      }
      api.getTrainingActions(
        (data) => {
          this.trainingActions = data || []
        },
        () => {}
      )
    },
    filterProjectsByProfile(items) {
      const profile = gwt.getSessionProfile ? gwt.getSessionProfile() : null
      if (!profile || !profile.projects || profile.projects.trim() === '') {
        return items
      }
      const allowedIds = profile.projects.split(',').map((id) => id.trim())
      return items.filter((item) => allowedIds.includes(String(item.id)))
    },
    loadEmployeesFromDepartments() {
      const allEmployees = []
      ;(this.departments || []).forEach((department) => {
        if (department && Array.isArray(department.employees)) {
          allEmployees.push(...department.employees)
        }
      })

      const profile = gwt.getSessionProfile ? gwt.getSessionProfile() : null
      let allowedDepartments = []
      let allowedWorkplaces = []
      let allowedLegalEntities = []
      let onlyMyTeam = false

      if (profile) {
        if (profile.departments) {
          allowedDepartments = profile.departments.split(',').map(Number)
        }
        if (profile.workplaces) {
          allowedWorkplaces = profile.workplaces.split(',').map(Number)
        }
        if (profile.legalEntities) {
          allowedLegalEntities = profile.legalEntities.split(',').map(Number)
        }
        onlyMyTeam = !!profile.onlyMyTeam
      }

      const myTeamIds = onlyMyTeam && gwt.getSessionInformation()?.myTeam
        ? gwt.getSessionInformation().myTeam.map((item) => item.id)
        : []

      this.employees = allEmployees
        .filter((employee) => {
          const byDepartment = allowedDepartments.length === 0 || allowedDepartments.includes(employee.departmentId)
          const byWorkplace = allowedWorkplaces.length === 0 || allowedWorkplaces.includes(employee.workplaceId)
          const byLegalEntity = allowedLegalEntities.length === 0 || allowedLegalEntities.includes(employee.legalEntityId)
          const byMyTeam = !onlyMyTeam || myTeamIds.includes(employee.id)
          return byDepartment && byWorkplace && byLegalEntity && byMyTeam
        })
        .sort((a, b) => {
          const nameA = `${a.lastname || ''}, ${a.firstname || ''}`.toLowerCase()
          const nameB = `${b.lastname || ''}, ${b.firstname || ''}`.toLowerCase()
          return nameA.localeCompare(nameB)
        })
    },
    validateEditableParams() {
      const missingField = this.editableFieldDefinitions.find((field) => (
        field.required && (this.editableParams[field.key] == null || this.editableParams[field.key] === '')
      ))
      if (!missingField) {
        return true
      }
      this.$emit('show-alert', {
        text: `${this.getParamLabel(missingField.key)}: ${this.$t('wkt.admin.common.validation-utils.required_field')}`,
        color: 'error',
        timeout: 4000
      })
      return false
    },
    buildRequest() {
      const mergedParams = { ...this.reportParams }
      this.editableFieldDefinitions.forEach((field) => {
        const value = this.editableParams[field.key]
        if (value == null || value === '') {
          delete mergedParams[field.key]
        } else {
          mergedParams[field.key] = value
        }
      })
      const selectedReportType = this.getSelectedReportType()
      return {
        reportId: selectedReportType ? selectedReportType.id : this.reportType.id,
        locale: this.$i18n.locale,
        params: mergedParams
      }
    },
    getSelectedReportType() {
      const selectedFormat = this.selectedFormat || this.inferDefaultFormat()
      const related = (this.relatedReportTypes || []).find((reportType) => this.getFormatFromMimeType(reportType.downloadMimeType) === selectedFormat)
      return related || this.reportType
    },
    normalizeFields(fields) {
      return String(fields || '')
        .split(',')
        .map((field) => field.trim())
        .filter((field) => !!field)
        .sort()
        .join(',')
    },
    resolveRelatedReportTypes() {
      if (!this.reportType || !this.reportType.module) {
        this.selectedFormat = this.inferDefaultFormat()
        this.loading = false
        return
      }
      api.getAnalyticsReportTypes(
        this.reportType.module,
        (data) => {
          const all = data || []
          const currentFields = this.normalizeFields(this.reportType.fields)
          this.relatedReportTypes = all
            .filter((candidate) => candidate && candidate.enabled)
            .filter((candidate) => candidate.report === this.reportType.report)
            .filter((candidate) => this.normalizeFields(candidate.fields) === currentFields)
          if (!this.relatedReportTypes.find((candidate) => candidate.id === this.reportType.id)) {
            this.relatedReportTypes.push(this.reportType)
          }
          this.selectedFormat = this.inferDefaultFormat()
          this.loading = false
        },
        () => {
          this.relatedReportTypes = this.reportType ? [this.reportType] : []
          this.selectedFormat = this.inferDefaultFormat()
          this.loading = false
        }
      )
    },
    downloadReport() {
      if (!this.reportType || !this.reportType.id) {
        return
      }
      if (!this.validateEditableParams()) {
        return
      }

      this.downloading = true
      api.downloadAnalyticsReport(
        this.buildRequest(),
        () => {
          this.downloading = false
          this.showDialog = false
        },
        (error) => {
          this.downloading = false
          this.$emit('show-alert', {
            text: error?.response?.data?.message || this.$t('wkt.admin.reports.download_dialog.error.download'),
            color: 'error',
            timeout: 4000
          })
        }
      )
    }
  }
}
</script>
